import {
  type ForwardedRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef
} from 'react';
import type { CountdownFinishHandler, TimerAPI } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import { useInterval } from './use-interval';
import { usePersistentNumberCounter } from './use-persistent-number-counter';

interface UseNumberCounterProps {
  onCountdownFinish: CountdownFinishHandler;
  onCountdownReset: () => void;
  ref?: ForwardedRef<TimerAPI>;
}

export const useNumberCounter = ({
  ref,
  onCountdownFinish,
  onCountdownReset
}: UseNumberCounterProps) => {
  const {
    timerStartNumber,
    timerEndNumber,
    timerCountdownMode,
    timerCountdownFrequency,
    editMode,
    renderThumbnailMode
  } = useSettings();
  const {
    value,
    initialValue,
    interval: persistentAutoInterval,
    persistCounterState
  } = usePersistentNumberCounter();
  const calledCallbackRef = useRef(false);

  const isCountingUp = timerStartNumber < timerEndNumber;
  const isValueOutOfRange = isCountingUp
    ? value > timerEndNumber
    : value < timerEndNumber;
  const isAutoCountdown = timerCountdownMode === 'auto';
  const interval = isAutoCountdown
    ? timerCountdownMode
    : timerCountdownFrequency * 1000;

  const reset = useCallback(() => {
    persistCounterState({ value: timerStartNumber });
    calledCallbackRef.current = false;
    onCountdownReset();
  }, [timerStartNumber, persistCounterState, onCountdownReset]);

  useEffect(() => {
    if (editMode) {
      reset();
    }
  }, [reset, timerEndNumber, editMode]);

  useImperativeHandle(ref, () => ({ reset }), [reset]);

  useLayoutEffect(() => {
    if (!isValueOutOfRange || calledCallbackRef.current) {
      return;
    }

    onCountdownFinish(initialValue >= timerEndNumber);
    calledCallbackRef.current = true;
  }, [isValueOutOfRange, onCountdownFinish, initialValue, timerEndNumber]);

  useInterval({
    callback: (newInterval) => {
      persistCounterState(({ value: previousValue }) => ({
        value: isCountingUp ? previousValue + 1 : previousValue - 1,
        interval: isAutoCountdown ? newInterval : undefined
      }));
    },
    interval: isValueOutOfRange || renderThumbnailMode ? null : interval,
    defaultAutoInterval: persistentAutoInterval
  });

  return isCountingUp
    ? Math.min(value, timerEndNumber)
    : Math.max(value, timerEndNumber);
};
